import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  SiLinux,
  SiHtml5,
  SiPython,
  SiFastapi,
  SiJavascript,
  SiReact,
  SiMongodb,
  SiGithub,
  SiDocker,
} from "react-icons/si";

import {
  FaBrain,
  FaCode,
  FaProjectDiagram,
  FaLaptopCode,
  FaComments,
  FaSun,
  FaBookOpen,
  FaCoffee,
  FaUtensils,
  FaWalking,
  FaSyncAlt,
  FaHome,
  FaMoon,
  FaGraduationCap,
  FaClipboardCheck,
  FaBuilding,
} from "react-icons/fa";

import "./StudyRoadmap.css";

const placementSteps = [
  {
    no: "01",
    title: "Career Positioning",
    image: "/placement/positioning.png",
    text: "Choose the right role and career direction based on your current skills, projects and experience.",
    points: [
      "Target role selection",
      "Skill-gap analysis",
      "Career direction",
      "Profile positioning",
    ],
  },
  {
    no: "02",
    title: "Resume, LinkedIn & GitHub",
    image: "/placement/resume-linkedin.png",
    text: "Build a professional profile that recruiters can understand quickly.",
    points: [
      "ATS-ready resume",
      "LinkedIn upgrade",
      "GitHub improvement",
      "Project presentation",
    ],
  },
  {
    no: "03",
    title: "Project Explanation",
    image: "/placement/project-demo.png",
    text: "Prepare to explain your projects, architecture, technical decisions and results confidently.",
    points: [
      "Project walkthrough",
      "Architecture explanation",
      "Challenges solved",
      "Demo preparation",
    ],
  },
  {
    no: "04",
    title: "Technical Interview",
    image: "/placement/technical-interview.png",
    text: "Prepare role-specific technical questions, coding rounds and project discussions.",
    points: [
      "Core subject revision",
      "Coding practice",
      "Technical questions",
      "Project questions",
    ],
  },
  {
    no: "05",
    title: "HR & Communication",
    image: "/placement/hr-communication.png",
    text: "Prepare your introduction, career story, communication and HR rounds.",
    points: [
      "Self-introduction",
      "Career story",
      "HR questions",
      "Salary discussion",
    ],
  },
  {
    no: "06",
    title: "Job Application Strategy",
    image: "/placement/job-application.png",
    text: "Apply to the right roles instead of randomly sending applications everywhere.",
    points: [
      "Target companies",
      "Suitable job roles",
      "Recruiter outreach",
      "Application tracking",
    ],
  },
  {
    no: "07",
    title: "Mock Interviews",
    image: "/placement/mock-interview.png",
    text: "Practice complete interview rounds and improve continuously using feedback.",
    points: [
      "Technical mocks",
      "HR mocks",
      "Panel review",
      "Weak-area correction",
    ],
  },
  {
    no: "08",
    title: "Final Push & Placement",
    image: "/placement/final-push.png",
    text: "Use interview feedback, follow-up and final preparation until you are ready to convert the right opportunity.",
    points: [
      "Interview feedback",
      "Follow-up strategy",
      "Offer discussion",
      "Joining readiness",
    ],
  },
];

const syllabusBase = [
  {
    no: "01",
    title: "Linux",
    baseDays: 4,
    icon: SiLinux,
    text: "Terminal, files, permissions, processes and developer workflow.",
  },
  {
    no: "02",
    title: "HTML",
    baseDays: 7,
    icon: SiHtml5,
    text: "Page structure, semantic HTML, forms, media and web fundamentals.",
  },
  {
    no: "03",
    title: "CSS",
    baseDays: 7,
    icon: FaCode,
    text: "Responsive layouts, Flexbox, Grid, UI styling and animations.",
  },
  {
    no: "04",
    title: "Python",
    baseDays: 18,
    icon: SiPython,
    text: "Programming, functions, OOP, files, errors, modules and problem solving.",
  },
  {
    no: "05",
    title: "FastAPI",
    baseDays: 8,
    icon: SiFastapi,
    text: "APIs, routing, validation, request/response models and backend development.",
  },
  {
    no: "06",
    title: "JavaScript",
    baseDays: 8,
    icon: SiJavascript,
    text: "Modern JavaScript, DOM, events, async programming and browser logic.",
  },
  {
    no: "07",
    title: "React.js",
    baseDays: 14,
    icon: SiReact,
    text: "Components, props, state, hooks, routing, forms and API integration.",
  },
  {
    no: "08",
    title: "MongoDB",
    baseDays: 6,
    icon: SiMongodb,
    text: "Collections, CRUD, schema design, aggregation and application integration.",
  },
  {
    no: "09",
    title: "GitHub",
    baseDays: 6,
    icon: SiGithub,
    text: "Repositories, commits, pull requests, README and portfolio presentation.",
  },
  {
    no: "10",
    title: "Artificial Intelligence",
    baseDays: 34,
    icon: FaBrain,
    text: "Machine Learning, Deep Learning, NLP, Computer Vision, Generative AI, LLMs and AI Agents.",
  },
  {
    no: "11",
    title: "Docker",
    baseDays: 8,
    icon: SiDocker,
    text: "Containers, images, Dockerfiles, volumes, networking and production deployment.",
  },
];


const buildSyllabusForDays = (totalDays) => {
  const baseTotal = syllabusBase.reduce(
    (sum, item) => sum + item.baseDays,
    0
  );

  const raw = syllabusBase.map((item) => {
    const exact = (item.baseDays / baseTotal) * totalDays;

    return {
      ...item,
      exact,
      allocatedDays: Math.floor(exact),
      remainder: exact - Math.floor(exact),
    };
  });

  let allocated = raw.reduce(
    (sum, item) => sum + item.allocatedDays,
    0
  );

  const remaining = totalDays - allocated;

  const remainderOrder = [...raw]
    .map((item, index) => ({
      index,
      remainder: item.remainder,
    }))
    .sort((a, b) => b.remainder - a.remainder);

  for (let i = 0; i < remaining; i++) {
    raw[remainderOrder[i].index].allocatedDays += 1;
  }

  let startDay = 1;

  return raw.map((item) => {
    const endDay =
      startDay + item.allocatedDays - 1;

    const result = {
      ...item,
      days:
        `Day ${startDay} – Day ${endDay}`,
      dayCount: item.allocatedDays,
    };

    startDay = endDay + 1;

    return result;
  });
};

const roadmapData = {
  graduate: {
    label:"Graduation Completed", title:"Graduate Career Roadmap", duration: "4 Months",
    totalDays: 120, theme:"theme-graduate",
    image: "/assets/roadmaps/graduate-roadmap.png",
    imageAlt:"Graduate students discussing their career roadmap",
    intro:"A focused full-time path from learning and projects to interview preparation and placement.",
    routine:[
      ["7:00 AM – 7:30 AM","Morning routine + daily planning","Life Balance"],
      ["7:30 AM – 9:30 AM","Guided learning","Study"],
      ["9:30 AM – 10:00 AM","Breakfast + refresh","Life Balance"],
      ["10:00 AM – 11:00 AM","Notes + concept revision","Study"],
      ["11:00 AM – 12:30 PM","Coding practice","Practice"],
      ["12:30 PM – 1:30 PM","Lunch + rest","Life Balance"],
      ["1:30 PM – 3:00 PM","Assignments / guided learning","Study"],
      ["3:00 PM – 3:30 PM","Break","Life Balance"],
      ["3:30 PM – 5:00 PM","Project building","Project"],
      ["5:00 PM – 6:00 PM","Doubt solving + revision","Practice"],
      ["6:00 PM – 8:00 PM","Family / dinner / personal time","Life Balance"],
      ["8:00 PM – 9:00 PM","Interview preparation + next-day planning","Interview"],
    ],
  },
  working: {
    label:"Working Professional", title:"Working Professional Career Upgrade", duration: "6 Months",
    totalDays: 180, theme:"theme-working",
    image: "/assets/roadmaps/working-professional-roadmap.png",
    imageAlt:"Working professional planning a career upgrade",
    intro:"A compact upgrade path designed around a full-time job, existing experience and a targeted role switch.",
    routine:[
      ["7:00 AM – 7:40 AM","Concept learning + notes","Study"],
      ["7:40 AM – 8:00 AM","Daily goal planning","Planning"],
      ["8:00 AM – 8:00 PM","Office / professional work","Work"],
      ["8:00 PM – 8:30 PM","Dinner + refresh","Life Balance"],
      ["8:30 PM – 9:15 PM","Coding / project work","Project"],
      ["9:15 PM – 9:45 PM","Interview preparation","Interview"],
      ["9:45 PM – 10:00 PM","Next-day planning","Planning"],
    ],
  },
  college: {
    label:"Studying in College", title:"College Student Career Roadmap", duration: "9 Months",
    totalDays: 270, theme:"theme-college",
    image: "/assets/roadmaps/college-student-roadmap.png",
    imageAlt:"College students learning and building projects together",
    intro:"Build job-ready skills, projects and interview confidence while continuing college normally.",
    routine:[
      ["7:00 AM – 7:30 AM","Concept revision / coding warm-up","Study"],
      ["7:30 AM – 8:00 AM","Breakfast + college preparation","Life Balance"],
      ["8:00 AM – 5:00 PM","College / practical work","College"],
      ["5:00 PM – 6:00 PM","Rest / commute","Life Balance"],
      ["6:00 PM – 7:00 PM","College assignments","College"],
      ["7:00 PM – 8:15 PM","Guided career learning","Study"],
      ["8:15 PM – 9:15 PM","Coding / project building","Project"],
      ["9:15 PM – 9:45 PM","Revision + interview basics","Interview"],
    ],
  },
  school: {
    label:"10th / 12th Completed", title:"Early Career Foundation Roadmap", duration: "3 Years",
    totalDays: 1095, theme:"theme-school",
    image: "/assets/roadmaps/school-student-roadmap.png",
    imageAlt:"Students learning in a classroom",
    intro:"Start early without compromising academics. Build coding, projects and career awareness progressively until graduation.",
    routine:[
      ["7:00 AM – 7:30 AM","Logic / coding foundation","Study"],
      ["7:30 AM – 8:00 AM","Breakfast + school preparation","Life Balance"],
      ["8:00 AM – 4:00 PM","School / junior college","School"],
      ["4:00 PM – 5:00 PM","Rest / refresh","Life Balance"],
      ["5:00 PM – 6:30 PM","Board / entrance preparation","School"],
      ["6:30 PM – 7:30 PM","Guided technical learning","Study"],
      ["7:30 PM – 8:30 PM","Dinner + family time","Life Balance"],
      ["8:30 PM – 9:15 PM","Coding / mini project","Project"],
      ["9:15 PM – 9:45 PM","Revision","Practice"],
    ],
  },
};


const getRoutineIcon = (task, type) => {
  const value = `${task} ${type}`.toLowerCase();

  if (value.includes("morning") || value.includes("planning")) return FaSun;
  if (value.includes("breakfast") || value.includes("refresh")) return FaCoffee;
  if (value.includes("lunch") || value.includes("dinner")) return FaUtensils;
  if (value.includes("college") || value.includes("school")) return FaGraduationCap;
  if (value.includes("office") || value.includes("work")) return FaBuilding;
  if (value.includes("project")) return FaProjectDiagram;
  if (value.includes("coding")) return FaLaptopCode;
  if (value.includes("interview")) return FaComments;
  if (value.includes("revision") || value.includes("doubt")) return FaSyncAlt;
  if (value.includes("assignment")) return FaClipboardCheck;
  if (value.includes("break") || value.includes("walk")) return FaWalking;
  if (value.includes("family")) return FaHome;
  if (value.includes("sleep")) return FaMoon;
  if (value.includes("learning") || value.includes("lecture")) return FaBookOpen;

  return FaBookOpen;
};

const order=["graduate","working","college","school"];

export default function StudyRoadmap(){
  const navigate=useNavigate();
  const [selected,setSelected]=useState("graduate");
  const roadmap=useMemo(()=>roadmapData[selected],[selected]);

  const roadmapSyllabus = useMemo(
    () => buildSyllabusForDays(roadmap.totalDays),
    [roadmap.totalDays]
  );

  const selectRoadmap=(key)=>{
    setSelected(key);
    window.setTimeout(()=>document.getElementById("roadmap-detail")?.scrollIntoView({behavior:"smooth",block:"start"}),80);
  };

  return (
    <main className={`roadmap-page ${roadmap.theme}`}>
      <section className="roadmap-top">
        <div className="roadmap-shell">
          <p className="roadmap-eyebrow">STUDY ROADMAP</p>
          <h1>Target Salary Up To ₹1 Crore / Year</h1>
          <p className="roadmap-lead">Choose your current stage to see the study routine, updated syllabus, and interview-to-placement roadmap.</p>

          <div className="roadmap-selector-wrap">
            <p className="selector-kicker"></p>
            <h2>Select Your Current Stage</h2>
            <div className="roadmap-selector">
              {order.map((key,index)=>{
                const item = roadmapData[key];

                return (
                  <button
                    key={key}
                    className={`roadmap-choice roadmap-choice-${key} ${
                      selected === key ? "active" : ""
                    }`}
                    onClick={() => selectRoadmap(key)}
                  >
                    <div className="roadmap-choice-top">
                      <span className="roadmap-choice-number">
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <span className="roadmap-choice-duration">
                        {item.duration}
                      </span>
                    </div>

                    <strong>{item.label}</strong>

                    <small>
                      View Roadmap →
                    </small>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <div id="roadmap-detail" className="roadmap-detail">
        <section className="roadmap-profile">
          <div className="roadmap-shell profile-grid">
            <div className="profile-photo"><img src={roadmap.image} alt={roadmap.imageAlt}/></div>
            <div className="profile-copy">
              <p className="section-label">SELECTED ROADMAP</p>
              <h2>{roadmap.title}</h2>
              <p>{roadmap.intro}</p>
              <div className="profile-metrics">
                <div><small>TARGET</small><strong>Up to ₹1 Crore / Year</strong></div>
                <div><small>DURATION</small><strong>{roadmap.duration}</strong></div>
              </div>
              <div className="profile-points">
                <span>Study Routine</span><span>Updated Syllabus</span><span>Interview Till Placement</span>
              </div>
            </div>
          </div>
        </section>

        <section className="roadmap-section">
          <div className="roadmap-shell">
            <div className="section-heading">
              <p className="section-label">01 — DAILY ROUTINE</p>
              <h2>Study Routine</h2>
              <p>A practical daily structure based on the time available in this category.</p>
            </div>
            <div className="routine-grid">
              {roadmap.routine.map(([time, task, type]) => {
                const RoutineIcon = getRoutineIcon(task, type);

                return (
                  <article
                    className="routine-card"
                    key={`${time}-${task}`}
                  >
                    <div className="routine-top">
                      <strong>{time}</strong>
                      <span>{type}</span>
                    </div>

                    <div className="routine-main">
                      <div className="routine-icon">
                        <RoutineIcon />
                      </div>

                      <h3>{task}</h3>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="roadmap-section syllabus-section">
          <div className="roadmap-shell">
            <div className="section-heading">
              <p className="section-label">02 — UPDATED SYLLABUS</p>
              <h2>Learn in the Right Sequence</h2>
              <p>The technical sequence stays consistent, but the number of days for each topic changes automatically according to your selected roadmap duration.</p>
            </div>
            <div className="syllabus-grid">
              {roadmapSyllabus.map(item=>(
                <article className="syllabus-card" key={item.no}>
                  <div className="syllabus-card-top">
                    <span className="syllabus-no">
                      PHASE {item.no}
                    </span>

                    <div className="syllabus-logo">
                      <item.icon />
                    </div>
                  </div>

                  <h3>{item.title}</h3>

                  <div className="syllabus-days">
                    {item.days}
                    <small>{item.dayCount} days</small>
                  </div>

                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="roadmap-section placement-section">
          <div className="roadmap-shell">
            <div className="section-heading">
              <p className="section-label">03 — INTERVIEW TILL PLACEMENT</p>
              <h2>From Learning to Placement</h2>
              <p>Technical learning is only one part of the journey. Students also prepare their profile, projects, interviews, applications and final placement readiness.</p>
            </div>
            <div className="placement-timeline">
              {placementSteps.map((item) => (
                <article className="placement-card" key={item.no}>
                  <div className="placement-number">
                    {item.no}
                  </div>

                  <div className="placement-visual">
                    <img
                      src={item.image}
                      alt={item.title}
                    />
                  </div>

                  <div className="placement-copy">
                    <p className="placement-step-label">
                      PLACEMENT STEP {Number(item.no)}
                    </p>

                    <h3>{item.title}</h3>

                    <p className="placement-description">
                      {item.text}
                    </p>

                    <div className="placement-points">
                      {item.points.map((point) => (
                        <span key={point}>
                          ✓ {point}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="roadmap-cta">
          <div className="roadmap-shell">
            <div className="cta-box">
              <div><p className="section-label">NEED PERSONAL GUIDANCE?</p><h2>Understand your exact starting point and next steps.</h2></div>
              <button onClick={()=>navigate(`/counselling?roadmap=${selected}`)}>Book Counselling</button>
            </div>
            <p className="roadmap-disclaimer">₹1 Crore/year is our ambitious long-term career target. Your success depends on your skills, dedication, performance, opportunities, and individual circumstances.</p>
          </div>
        </section>
      </div>
    </main>
  );
}

